import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';

interface KosikItem {
  id: number;
}

interface KosikContextType {
  items: KosikItem[];
  email: string;
  telefon: string;
  addItem: (id: number) => void;
  removeItem: (id: number) => void;
  isInKosik: (id: number) => boolean;
  clearKosik: () => void;
  setEmail: (email: string) => void;
  setTelefon: (telefon: string) => void;
  itemCount: number;
  expiresAt: number | null;
}

const KosikContext = createContext<KosikContextType | undefined>(undefined);

const COOKIE_NAMES = {
  ITEMS: 'zr_kosik_items',
  EMAIL: 'zr_kosik_email',
  TELEFON: 'zr_kosik_telefon',
  EXPIRES_AT: 'zr_kosik_expires_at',
};

const COOKIE_OPTIONS = {
  expires: 2/24, // 2 hodiny
  sameSite: 'strict' as const,
};

interface KosikProviderProps {
  children: ReactNode;
}

export const KosikProvider: React.FC<KosikProviderProps> = ({ children }) => {
  const [items, setItems] = useState<KosikItem[]>([]);
  const [email, setEmailState] = useState<string>('');
  const [telefon, setTelefonState] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  // Načtení dat z cookies při načtení komponenty
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (cookieConsent === 'accepted') {
      const savedItems = Cookies.get(COOKIE_NAMES.ITEMS);
      const savedEmail = Cookies.get(COOKIE_NAMES.EMAIL);
      const savedTelefon = Cookies.get(COOKIE_NAMES.TELEFON);
      const savedExpiresAt = Cookies.get(COOKIE_NAMES.EXPIRES_AT);

      if (savedItems) {
        try {
          setItems(JSON.parse(savedItems));
        } catch (e) {
          console.error('Chyba při parsování košíku z cookies:', e);
        }
      }

      if (savedEmail) {
        setEmailState(savedEmail);
      }

      if (savedTelefon) {
        setTelefonState(savedTelefon);
      }

      if (savedExpiresAt) {
        setExpiresAt(Number(savedExpiresAt));
      }
    }
  }, []);

  // Uložení položek do cookies při změně a reset časovače
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (cookieConsent === 'accepted') {
      if (items.length > 0) {
        Cookies.set(COOKIE_NAMES.ITEMS, JSON.stringify(items), COOKIE_OPTIONS);
        
        // Resetovat časovač 2 hodiny při každé změně v košíku
        const expirationTime = Date.now() + (2 * 60 * 60 * 1000);
        setExpiresAt(expirationTime);
        Cookies.set(COOKIE_NAMES.EXPIRES_AT, String(expirationTime), COOKIE_OPTIONS);
      } else {
        // Pokud je košík prázdný, vymazat cookies a expiraci
        Cookies.remove(COOKIE_NAMES.ITEMS);
        Cookies.remove(COOKIE_NAMES.EXPIRES_AT);
        setExpiresAt(null);
      }
    }
  }, [items]);

  const addItem = (id: number) => {
    if (!items.find(item => item.id === id)) {
      setItems([...items, { id }]);
    }
  };

  const removeItem = (id: number) => {
    setItems(items.filter(item => item.id !== id));
  };

  const isInKosik = (id: number): boolean => {
    return items.some(item => item.id === id);
  };

  const clearKosik = () => {
    setItems([]);
    Cookies.remove(COOKIE_NAMES.ITEMS);
    Cookies.remove(COOKIE_NAMES.EXPIRES_AT);
    setExpiresAt(null);
  };

  const setEmail = (newEmail: string) => {
    setEmailState(newEmail);
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (cookieConsent === 'accepted') {
      if (newEmail) {
        Cookies.set(COOKIE_NAMES.EMAIL, newEmail, COOKIE_OPTIONS);
      } else {
        Cookies.remove(COOKIE_NAMES.EMAIL);
      }
    }
  };

  const setTelefon = (newTelefon: string) => {
    setTelefonState(newTelefon);
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (cookieConsent === 'accepted') {
      if (newTelefon) {
        Cookies.set(COOKIE_NAMES.TELEFON, newTelefon, COOKIE_OPTIONS);
      } else {
        Cookies.remove(COOKIE_NAMES.TELEFON);
      }
    }
  };

  const value: KosikContextType = {
    items,
    email,
    telefon,
    addItem,
    removeItem,
    isInKosik,
    clearKosik,
    setEmail,
    setTelefon,
    itemCount: items.length,
    expiresAt,
  };

  return (
    <KosikContext.Provider value={value}>
      {children}
    </KosikContext.Provider>
  );
};

export const useKosik = (): KosikContextType => {
  const context = useContext(KosikContext);
  if (context === undefined) {
    throw new Error('useKosik must be used within a KosikProvider');
  }
  return context;
};
